import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import { api, API_BASE } from "@/lib/api";
import { useI18n } from "@/contexts/I18nContext";
import { toast } from "sonner";
import { Sparkles, Save, Trash2, Wand2, Hash, Lightbulb, Copy, Image as ImageIcon, X } from "lucide-react";

const PLATFORMS = ["instagram", "tiktok", "twitter", "linkedin", "youtube", "facebook"];

function AIGenerator({ onInsert }) {
  const { lang } = useI18n();
  const [topic, setTopic] = useState("");
  const [platform, setPlatform] = useState("instagram");
  const [tone, setTone] = useState("professional");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState({ caption: "", hashtags: "", ideas: "" });

  const gen = async (kind) => {
    if (!topic) { toast.error(lang === "ar" ? "أدخل موضوعاً" : "Enter a topic first"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/ai/generate", { kind, topic, platform, tone, language: lang });
      setResult((r) => ({ ...r, [kind]: data.result }));
    } catch (e) { toast.error("Generation failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="nx-card p-5 space-y-3" data-testid="ai-generator-panel">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-primary" />
        <div className="font-medium">{lang === "ar" ? "مولّد المحتوى الذكي" : "AI Content Generator"}</div>
      </div>
      <input data-testid="gen-topic" value={topic} onChange={(e) => setTopic(e.target.value)}
        placeholder={lang === "ar" ? "الموضوع (مثال: افتتاح مقهى)" : "Topic (e.g. new cafe opening)"}
        className="w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary" />
      <div className="grid grid-cols-2 gap-2">
        <select data-testid="gen-platform" value={platform} onChange={(e) => setPlatform(e.target.value)}
          className="bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary text-sm">
          {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select data-testid="gen-tone" value={tone} onChange={(e) => setTone(e.target.value)}
          className="bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary text-sm">
          {["professional", "friendly", "witty", "bold", "inspirational"].map((t) => <option key={t}>{t}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <button data-testid="gen-caption-btn" onClick={() => gen("caption")} disabled={busy} className="text-xs py-2 border border-border rounded-sm hover:bg-muted flex items-center justify-center gap-1"><Wand2 className="w-3 h-3" /> {lang === "ar" ? "تعليقات" : "Captions"}</button>
        <button data-testid="gen-hashtags-btn" onClick={() => gen("hashtags")} disabled={busy} className="text-xs py-2 border border-border rounded-sm hover:bg-muted flex items-center justify-center gap-1"><Hash className="w-3 h-3" /> {lang === "ar" ? "هاشتاغات" : "Hashtags"}</button>
        <button data-testid="gen-ideas-btn" onClick={() => gen("ideas")} disabled={busy} className="text-xs py-2 border border-border rounded-sm hover:bg-muted flex items-center justify-center gap-1"><Lightbulb className="w-3 h-3" /> {lang === "ar" ? "أفكار" : "Ideas"}</button>
      </div>
      {["caption", "hashtags", "ideas"].map((k) =>
        result[k] ? (
          <div key={k} data-testid={`gen-result-${k}`} className="border border-border rounded-sm p-3 text-xs">
            <div className="flex justify-between items-center mb-2">
              <span className="uppercase tracking-widest text-muted-foreground">{k}</span>
              <div className="flex gap-1">
                <button data-testid={`gen-insert-${k}`} onClick={() => onInsert(k, result[k])} className="text-primary text-[10px] hover:underline">{lang === "ar" ? "إدراج" : "Insert"}</button>
                <button onClick={() => { navigator.clipboard.writeText(result[k]); toast.success("Copied"); }} className="text-muted-foreground"><Copy className="w-3 h-3" /></button>
              </div>
            </div>
            <div className="whitespace-pre-wrap leading-relaxed">{result[k]}</div>
          </div>
        ) : null
      )}
    </div>
  );
}

export default function ContentStudioPage() {
  const { lang } = useI18n();
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({ title: "", content: "", hashtags: "", platforms: [], status: "draft", scheduled_at: "", media_urls: [] });

  const load = async () => setPosts((await api.get("/posts")).data.posts);
  useEffect(() => { load(); }, []);

  const resetForm = () => { setForm({ title: "", content: "", hashtags: "", platforms: [], status: "draft", scheduled_at: "", media_urls: [] }); setEditing(null); };

  const save = async (status) => {
    if (!form.title || !form.content) { toast.error("Title & content required"); return; }
    // Server ignores client-supplied status/approval_status — the schema
    // whitelists user-safe fields only. We still keep the local status hint
    // in the button label for UX clarity.
    const payload = {
      title: form.title, content: form.content,
      hashtags: form.hashtags, platforms: form.platforms,
      scheduled_at: form.scheduled_at || null,
      media_urls: form.media_urls || [],
    };
    try {
      if (editing) await api.put(`/posts/${editing}`, payload);
      else await api.post("/posts", payload);
      toast.success(lang === "ar" ? "تم الحفظ" : "Saved");
      resetForm(); load();
    } catch (e) {
      const d = e.response?.data?.detail || {};
      toast.error(d.message_ar || d.message_en || "Save failed");
    }
  };

  const submitForReview = async (id) => {
    try {
      await api.post(`/posts/${id}/submit-for-review`);
      toast.success(lang === "ar" ? "تم إرسال المنشور للمراجعة" : "Submitted for review");
      load();
    } catch (e) {
      const d = e.response?.data?.detail || {};
      toast.error(d.message_ar || d.message_en || "Failed to submit");
    }
  };

  const APPROVAL_LABEL = {
    draft:          { ar: "مسودة",             en: "Draft",           cls: "border-neutral-300 text-neutral-600" },
    pending_review: { ar: "بانتظار المراجعة",  en: "Awaiting review", cls: "border-amber-400 text-amber-700 bg-amber-50" },
    approved:       { ar: "معتمَد",            en: "Approved",         cls: "border-emerald-400 text-emerald-700 bg-emerald-50" },
    rejected:       { ar: "مرفوض",             en: "Rejected",         cls: "border-red-400 text-red-700 bg-red-50" },
  };

  const isLocked = (p) =>
    p.approval_status === "pending_review" || p.approval_status === "approved";

  const edit = (p) => { setEditing(p.id); setForm({ title: p.title, content: p.content, hashtags: p.hashtags || "", platforms: p.platforms || [], status: p.status, scheduled_at: p.scheduled_at || "", media_urls: p.media_urls || [] }); };
  const del = async (id) => { await api.delete(`/posts/${id}`); load(); };

  const togglePlatform = (p) => setForm((f) => ({
    ...f, platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p],
  }));

  const insertAI = (kind, text) => {
    if (kind === "hashtags") setForm((f) => ({ ...f, hashtags: text }));
    else setForm((f) => ({ ...f, content: (f.content ? f.content + "\n\n" : "") + text }));
    toast.success("Inserted");
  };

  const uploadMedia = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(lang === "ar" ? "الحد الأقصى ٥ ميغابايت" : "Max 5 MB"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    const token = localStorage.getItem("nx_token");
    try {
      const resp = await fetch(`${API_BASE}/uploads`, {
        method: "POST",
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: fd,
      });
      if (!resp.ok) throw new Error((await resp.json()).detail || "Failed");
      const { file: uploaded } = await resp.json();
      setForm((f) => ({ ...f, media_urls: [...(f.media_urls || []), uploaded.storage_path] }));
      toast.success(lang === "ar" ? "تم رفع الصورة" : "Uploaded");
    } catch (e) {
      toast.error(e.message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeMedia = (path) =>
    setForm((f) => ({ ...f, media_urls: (f.media_urls || []).filter((p) => p !== path) }));

  const mediaUrl = (path) => {
    const token = localStorage.getItem("nx_token") || "";
    return `${API_BASE}/files/${path}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
  };

  return (
    <AppShell>
      <div className="mb-6">
        <h1 className="font-display text-3xl">{lang === "ar" ? "استوديو المحتوى" : "Content Studio"}</h1>
        <p className="text-muted-foreground text-sm mt-1">{lang === "ar" ? "أنشئ، احفظ، وجدول منشوراتك عبر جميع القنوات." : "Create, save, and schedule posts across every channel."}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <div className="nx-card p-5" data-testid="post-editor">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{editing ? (lang === "ar" ? "تعديل منشور" : "Edit post") : (lang === "ar" ? "منشور جديد" : "New post")}</div>
            <input data-testid="post-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder={lang === "ar" ? "عنوان المنشور" : "Post title"}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary mb-3" />
            <textarea data-testid="post-content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })}
              rows={8} placeholder={lang === "ar" ? "اكتب المحتوى..." : "Write your content..."}
              className="w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary font-mono text-sm mb-3" />
            <input data-testid="post-hashtags" value={form.hashtags} onChange={(e) => setForm({ ...form, hashtags: e.target.value })}
              placeholder="#hashtags"
              className="w-full bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary mb-3" />

            <div className="mb-3">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{lang === "ar" ? "المنصات" : "Platforms"}</div>
              <div className="flex flex-wrap gap-2">
                {PLATFORMS.map((p) => (
                  <button key={p} type="button" data-testid={`post-platform-${p}`} onClick={() => togglePlatform(p)}
                    className={`px-3 py-1.5 text-xs rounded-sm border transition ${form.platforms.includes(p) ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}>
                    {p}
                  </button>
                ))}
              </div>
            </div>

            <div className="mb-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{lang === "ar" ? "الوسائط" : "Media"}</div>
              <div className="flex flex-wrap items-start gap-2">
                {(form.media_urls || []).map((path) => (
                  <div key={path} data-testid={`post-media-${path.split("/").pop()}`} className="relative w-20 h-20 rounded-sm overflow-hidden border border-border group">
                    <img src={mediaUrl(path)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeMedia(path)}
                      className="absolute top-0 end-0 bg-black/60 text-white p-0.5 opacity-0 group-hover:opacity-100 transition">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <label className="w-20 h-20 border border-dashed border-border rounded-sm flex flex-col items-center justify-center cursor-pointer hover:bg-muted/40 text-muted-foreground text-[10px] gap-1">
                  <input ref={fileInputRef} data-testid="post-upload-input" type="file" accept="image/*" hidden
                    onChange={(e) => uploadMedia(e.target.files?.[0])} />
                  {uploading ? "…" : <ImageIcon className="w-4 h-4" />}
                  <span>{uploading ? (lang === "ar" ? "جاري" : "Uploading") : (lang === "ar" ? "أضف" : "Add")}</span>
                </label>
              </div>
            </div>

            <div className="mb-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">{lang === "ar" ? "جدولة (اختياري)" : "Schedule (optional)"}</div>
              <input type="datetime-local" data-testid="post-scheduled" value={form.scheduled_at || ""} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
                className="bg-background border border-border rounded-sm px-3 py-2 outline-none focus:border-primary" />
            </div>

            <div className="flex flex-wrap gap-2">
              <button data-testid="post-save-draft-btn" onClick={() => save("draft")} className="nx-btn-secondary inline-flex items-center gap-2"><Save className="w-4 h-4" /> {lang === "ar" ? "حفظ كمسودة" : "Save draft"}</button>
              {editing && <button data-testid="post-cancel-btn" onClick={resetForm} className="text-sm text-muted-foreground px-3">{lang === "ar" ? "إلغاء" : "Cancel"}</button>}
            </div>
          </div>

          <div className="nx-card p-5" data-testid="posts-list">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">{lang === "ar" ? "منشوراتي" : "My posts"} · {posts.length}</div>
            {posts.length === 0 && <div className="text-sm text-muted-foreground py-4">{lang === "ar" ? "لا منشورات بعد." : "No posts yet."}</div>}
            <div className="space-y-2">
              {posts.map((p) => {
                const badge = APPROVAL_LABEL[p.approval_status] || APPROVAL_LABEL.draft;
                const locked = isLocked(p);
                return (
                  <div key={p.id} data-testid={`post-item-${p.id}`} className="flex items-start justify-between gap-3 p-3 border border-border/60 rounded-sm hover:bg-muted/30">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border ${badge.cls}`}
                              data-testid={`post-approval-${p.id}`}>
                          {badge[lang] || badge.en}
                        </span>
                        <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 border border-border rounded-sm">{p.status}</span>
                        <span className="font-medium truncate">{p.title}</span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{p.content}</div>
                      {p.approval_status === "rejected" && p.reject_reason && (
                        <div className="text-[11px] text-red-700 mt-1">
                          {lang === "ar" ? "سبب الرفض: " : "Reason: "}{p.reject_reason}
                        </div>
                      )}
                      {p.scheduled_at && <div className="text-[10px] text-primary mt-1">📅 {new Date(p.scheduled_at).toLocaleString()}</div>}
                      {p.platform_results?.length > 0 && (
                        <div className="text-[10px] text-emerald-700 mt-1">
                          {p.platform_results.filter(r => r.success).map(r => (
                            <a key={r.platform} href={r.permalink} target="_blank" rel="noreferrer"
                               className="me-2 underline">
                              {r.platform}: {r.media_id}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col gap-1 items-end">
                      {p.approval_status === "draft" || p.approval_status === "rejected" ? (
                        <button data-testid={`post-submit-review-${p.id}`}
                                onClick={() => submitForReview(p.id)}
                                className="text-[11px] px-2 py-0.5 rounded-full bg-amber-500 text-white hover:bg-amber-600">
                          {lang === "ar" ? "أرسل للمراجعة" : "Submit for review"}
                        </button>
                      ) : null}
                      <div className="flex gap-1">
                        <button data-testid={`post-edit-${p.id}`}
                                onClick={() => !locked && edit(p)}
                                disabled={locked}
                                className="text-xs text-primary hover:underline disabled:opacity-30 disabled:no-underline">
                          Edit
                        </button>
                        <button data-testid={`post-delete-${p.id}`}
                                onClick={() => del(p.id)}
                                className="text-xs text-destructive hover:underline">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <AIGenerator onInsert={insertAI} />
        </div>
      </div>
    </AppShell>
  );
}
